import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";
import logo from "../../assets/Main_Logo_AI_Colored_Padding.png"; // adjust path if needed

// ✅ Define Styles
const styles = StyleSheet.create({
  page: { padding: 20, fontSize: 12, fontFamily: "Helvetica" },
  header: { fontSize: 18, marginBottom: 10, fontWeight: "bold", textAlign: "center", color: "rgb(0, 145, 70)" },
  section: { marginBottom: 10, padding: 10, border: "1px solid #ddd", borderRadius: 6 },
  boldText: { fontWeight: "bold", fontSize: 14 },
  chipContainer: { flexDirection: "row", gap: 10, marginTop: 5, marginBottom: 10 },
  chip: { backgroundColor: "#267E3E", color: "white", padding: "3px 10px", borderRadius: 5, fontSize: 12 },
  listItem: { fontSize: 12, marginLeft: 10 },
  table: { display: "table", width: "100%", marginTop: 10, border: "1px solid #ddd" },
  tableRow: { flexDirection: "row", borderBottom: "1px solid #ddd" },
  tableHeader: { fontWeight: "bold", backgroundColor: "#f8f9fa", padding: 5 },
  tableCell: { flex: 1, padding: 5, fontSize: 11 },

  // ✅ Footer styles
  footer: {
    position: "absolute",
    bottom: 16,
    left: 20,
    right: 20,
    height: 24,
    borderTop: "1px solid #ccc",
    paddingTop: 6,
    fontSize: 9,
    color: "#6b7280",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  footerLogo: { width: 60 },
});

// ✅ Footer Component
const Footer = () => {
  const dateText = new Date().toLocaleDateString("en-US");
  return (
    <View style={styles.footer} fixed>
      <Text>{`Generated by Portend AI • Confidential • ${dateText}`}</Text>
      <Image style={styles.footerLogo} src={logo} />
    </View>
  );
};

// ✅ PDF Component
const MailServerPDF = ({ data, pageTitle }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Title */}
      <Text style={styles.header}>{pageTitle}</Text>

      {/* Target & Report Date */}
      <View style={styles.section}>
        <Text style={styles.boldText}>Target: <Text>{data?.domain || data?.target}</Text></Text>
        <Text style={styles.listItem}>Report Date: {moment(data?.date).format("MMM DD, YYYY HH:mm")}</Text>
      </View>

      {/* Grades Summary */}
      <View style={styles.section}>
        <Text style={styles.boldText}>Grades Summary:</Text>
        <View style={styles.chipContainer}>
          {Object.entries(data.grades).map(([key, value]) =>
            key !== "reason" && (
              <Text key={key} style={styles.chip}>{`${key.replace("Grade", "").toUpperCase()}: ${value}`}</Text>
            )
          )}
        </View>
      </View>

      {/* Server Latency */}
      <View style={styles.section}>
        <Text style={styles.boldText}>Server Latency:</Text>
        <Text style={styles.listItem}>
          {data.result?.serverLatency ? `${data.result.serverLatency}` : "No latency data available"}
        </Text>
      </View>

      {/* Security Warnings */}
      <View style={styles.section}>
        <Text style={styles.boldText}>Security Warnings & Recommendations:</Text>
        {data.grades?.reason.map((reason, i) => (
          <Text key={i} style={styles.listItem}>⚠ {reason}</Text>
        ))}
      </View>

      {/* PTR Records */}
      <View style={styles.section}>
        <Text style={styles.boldText}>PTR Info:</Text>
        {data.result?.ptrInfo.map((ptr, i) => (
          <Text key={i} style={styles.listItem}>{ptr}</Text>
        ))}
      </View>

      {/* MX Records */}
      <View style={styles.section}>
        <Text style={styles.boldText}>MX Records:</Text>
        {data.result?.newMxRecords?.mxRecords.length > 0 ? (
          data.result.newMxRecords.mxRecords.map((record, i) => (
            <Text key={i} style={styles.listItem}>{record}</Text>
          ))
        ) : (
          <Text>No MX records found.</Text>
        )}
      </View>

      {/* Mail Blacklist Info */}
      <View style={styles.section}>
        <Text style={styles.boldText}>Mail Blacklist Information:</Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.tableHeader]}>IP</Text>
            <Text style={[styles.tableCell, styles.tableHeader]}>Blacklisted</Text>
            <Text style={[styles.tableCell, styles.tableHeader]}>Detected By</Text>
          </View>
          {data.result?.mailBlackListInfo.map((entry, i) => (
            <View key={i} style={styles.tableRow}>
              <Text style={styles.tableCell}>{entry.ip}</Text>
              <Text style={styles.tableCell}>{entry.blacklisted ? "Yes" : "No"}</Text>
              <Text style={styles.tableCell}>
                {entry["detected-by"].length > 0 ? entry["detected-by"].join(", ") : "None"}
              </Text>
            </View>
          ))}
        </View>
      </View>

   

      {/* ✅ Fixed Footer */}
      <Footer />
    </Page>
    <Page size="A4" style={styles.page}>
      {/* SPF, DKIM, DMARC, BIMI, TLS-RPT, MTA-STS Details */}
      <View style={styles.section}>
        <Text style={styles.boldText}>Email Security Records:</Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.tableHeader]}>Type</Text>
            <Text style={[styles.tableCell, styles.tableHeader]}>Record</Text>
            <Text style={[styles.tableCell, styles.tableHeader]}>Status</Text>
            <Text style={[styles.tableCell, styles.tableHeader]}>Warnings</Text>
          </View>
          {["spfInfo", "dkimInfo", "dmarcInfo", "bimiInfo", "tlsrptInfo", "mtastsInfo"].map((recordType, i) => (
            <View key={i} style={styles.tableRow}>
              <Text style={styles.tableCell}>{recordType.replace("Info", "").toUpperCase()}</Text>
              <Text style={styles.tableCell}>{data.result[recordType]?.[`${recordType}Record`] || "Not found"}</Text>
              <Text style={styles.tableCell}>{data.result[recordType]?.status || "Unknown"}</Text>
              <Text style={styles.tableCell}>
                {data.result[recordType]?.warnings?.length > 0
                  ? data.result[recordType]?.warnings.join(", ")
                  : "No warnings"}
              </Text>
            </View>
          ))}
        </View>
      </View>
      {/* ✅ Fixed Footer */}
      <Footer />
    </Page>
  </Document>
);

export default MailServerPDF;
